<template>
  <DatePicker
    v-bind="$attrs"
    format="yyyy-MM-dd, HH:mm:ss"
    enable-seconds
    enable-time-picker
    month-change-on-arrows
    model-type="timestamp"
    :class="`size-${size}`"
    :inline-with-input="props.inlineWithInput"
    :inline="props.inline"
    :month-change-on-scroll="false"
    :select-text="t('filter.dateTime.select')"
    :cancel-text="t('filter.dateTime.cancel')"
    :placeholder="getPlaceHolder()"
    :model-value="convertedModelValue"
    :min-date="props.minDate"
    :no-disabled-range="true"
    :partial-range="true"
    input-class-name="dp-custom-input"
    @update:model-value="onSelectedDateChange"
    @cleared="emit('update:modelValue', null)"
    @time-picker-open="onTimePickerOpen"
  >
    <!-- Compact "Start: 15:00  End: 18:10" row instead of the clock-flip time picker -->
    <template v-if="showTime" #time-picker="{ time, updateTime }">
      <div class="flex items-center justify-center gap-4 px-2 py-1.5 text-xs text-neutral-600">
        <label class="flex items-center gap-1.5">
          <span class="font-medium">{{ t(isRangeTime(time) ? 'filter.dateTime.start' : 'filter.dateTime.time') }}:</span>
          <input
            type="time"
            class="dp-time-field"
            :value="timeFieldValue(time, 0)"
            @change="onTimeFieldChange($event, time, updateTime, 0)"
          />
        </label>
        <label v-if="isRangeTime(time)" class="flex items-center gap-1.5">
          <span class="font-medium">{{ t('filter.dateTime.end') }}:</span>
          <input
            type="time"
            class="dp-time-field"
            :value="timeFieldValue(time, 1)"
            @change="onTimeFieldChange($event, time, updateTime, 1)"
          />
        </label>
      </div>
    </template>
  </DatePicker>
</template>

<script lang="ts" setup>
import { isNotToday } from '@infuse/utils';
import DatePicker from '@vuepic/vue-datepicker';
import dayjs from 'dayjs';
import { computed, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';
import { DatePickerDate } from './InfuseDatePicker.types';

const { t } = useI18n();

interface Emits {
  (e: 'update:modelValue', value: DatePickerDate): void;
}

interface Props {
  size?: 'xs' | 'small' | 'medium' | 'large';
  extendRange?: boolean; // extend range(s) to full day(s)
  inline?: boolean;
  inlineWithInput?: boolean;
  minDate?: Date;
  modelValue?: DatePickerDate;
  placeholder?: string;
  showTime?: boolean; // compact always-visible time fields instead of the clock-flip picker
}

const props = withDefaults(defineProps<Props>(), {
  size: 'xs',
  extendRange: true,
  inline: false,
  inlineWithInput: true,
  minDate: undefined,
  modelValue: null,
  placeholder: undefined,
  showTime: false
});

const emit = defineEmits<Emits>();

const convertedModelValue = computed(() => convertToTimestamp(props.modelValue));
let isClosed = true;

function onTimePickerOpen() {
  isClosed = false;
}

function onSelectedDateChange(modelValue: number | [number, number | null] | null) {
  const newDates = convertToDates(modelValue);
  if (!newDates) {
    emit('update:modelValue', null);
    return;
  }

  if (Array.isArray(newDates) && isClosed && props.extendRange) {
    // extend range to full day
    if (newDates != props.modelValue) {
      newDates[0] = dayjs(newDates[0]).startOf('day').toDate();

      if (newDates[1] && isNotToday(newDates[1])) {
        newDates[1] = dayjs(newDates[1]).endOf('day').toDate();
      }
    }
  }

  emit('update:modelValue', newDates);
  isClosed = true;
}

function convertToDates(value: number | [number, number | null] | null): DatePickerDate {
  if (value === null) {
    return null;
  } else if (Array.isArray(value)) {
    return [new Date(value[0]), value[1] ? new Date(value[1]) : null];
  } else {
    return new Date(value);
  }
}

function hasRangeProp(): boolean {
  return useAttrs().hasOwnProperty('range');
}

function getPlaceHolder(): string {
  if (props.placeholder) {
    return props.placeholder;
  } else if (hasRangeProp()) {
    return t('filter.dateTime.range.placeholder');
  } else {
    return t('filter.dateTime.placeholder');
  }
}

/* showTime slot: compact time fields wired to the picker's own updateTime, so edits behave
   exactly like the built-in time picker (committed on select / close, same as before). */
type SlotTime = { hours: number | number[]; minutes: number | number[]; seconds: number | number[] };
type UpdateTime = (value: number | number[], isHours?: boolean, isSeconds?: boolean) => void;

function isRangeTime(time: SlotTime): boolean {
  return Array.isArray(time.hours);
}

function timeFieldValue(time: SlotTime, index: number): string {
  const get = (v: number | number[]) => (Array.isArray(v) ? v[index] : v) ?? 0;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(get(time.hours))}:${pad(get(time.minutes))}`;
}

function onTimeFieldChange(event: Event, time: SlotTime, updateTime: UpdateTime, index: number): void {
  const value = (event.target as HTMLInputElement).value;
  if (!value) {
    return;
  }
  const [hours, minutes] = value.split(':').map(Number);
  isClosed = false; // editing a time counts as using the time picker (disables full-day extendRange)
  if (Array.isArray(time.hours)) {
    const h = [...time.hours];
    h[index] = hours;
    updateTime(h, true, false);
    const m = [...(time.minutes as number[])];
    m[index] = minutes;
    updateTime(m, false, false);
  } else {
    updateTime(hours, true, false);
    updateTime(minutes, false, false);
  }
}

function convertToTimestamp(value: DatePickerDate): number | [number, number | null] | null {
  if (!value) {
    return null;
  } else if (Array.isArray(value)) {
    if (value.length !== 2 || !value[0]) {
      return null;
    } else {
      return [value[0].getTime(), value[1]?.getTime() ?? null];
    }
  } else {
    return value.getTime();
  }
}
</script>

<style scoped lang="scss">
:deep(.dp__selection_preview) {
  display: none;
}

:deep(.dp__action_buttons) {
  flex: 1;
  justify-content: space-between;
}

:deep(.dp__action_button) {
  @apply flex h-fit items-center justify-center rounded-md text-xs font-bold;
  @apply py-2 px-4;
  transition: none;
}

:deep(.dp__action_select) {
  @apply flex h-fit items-center justify-center rounded-md text-xs font-bold;
  @apply py-2 px-4;
  @apply bg-secondary-500 text-white hover:bg-secondary-400 active:bg-secondary-600 active:text-white disabled:bg-primary-50 disabled:text-neutral-300;
}

:deep(.dp__action_cancel) {
  @apply flex h-fit items-center justify-center rounded-md text-xs font-bold;
  @apply py-2 px-4;
  @apply border border-secondary-500 bg-white text-secondary-500 hover:border-secondary-300 hover:text-secondary-300 active:border-secondary-500 active:bg-secondary-500 active:text-white disabled:border-secondary-100 disabled:text-secondary-100;
}

:deep(.dp-custom-input) {
  @apply flex w-full items-center rounded-md border border-neutral-300 bg-neutral-0;
  @apply pl-8 pr-4;
  @apply hover:border-secondary-500 active:border-secondary-500;
  @apply transition-colors duration-150 ease-out-quart;
}

.size-xs :deep(.dp-custom-input) {
  @apply py-2 text-xs;
}

.size-small :deep(.dp-custom-input) {
  @apply py-2.5 text-xs;
}

.size-medium :deep(.dp-custom-input) {
  @apply py-4 text-sm;
}

.size-large :deep(.dp-custom-input) {
  @apply py-4 text-base;
}

:deep(.dp-custom-input:focus) {
  @apply border-secondary-500 shadow-[0px_0px_4px_theme('colors.primary.500')];
  outline: none;
}

.dp-time-field {
  @apply rounded-md border border-neutral-300 bg-neutral-0 px-2 py-1 text-xs text-neutral-800;
  @apply hover:border-secondary-500 focus:border-secondary-500;
  outline: none;
}
</style>

<style>
:root {
  --dp-font-family: Manrope, sans-serif !important;
  --dp-font-size: 0.875rem !important;
  --dp-action-buttons-padding: 0rem 1rem !important;
}
</style>

<i18n>
{
  "en": {
    "filter.dateTime.placeholder": "Select date",
    "filter.dateTime.range.placeholder": "Select date range",
    "filter.dateTime.cancel": "Cancel",
    "filter.dateTime.select": "Select",
    "filter.dateTime.now": "Now",
    "filter.dateTime.from": "From",
    "filter.dateTime.to": "To",
    "filter.dateTime.start": "Start",
    "filter.dateTime.end": "End",
    "filter.dateTime.time": "Time"
  },
  "nl": {
    "filter.dateTime.placeholder": "Kies datum",
    "filter.dateTime.range.placeholder": "Kies datumbereik",
    "filter.dateTime.cancel": "Annuleren",
    "filter.dateTime.select": "Selecteren",
    "filter.dateTime.now": "Nu",
    "filter.dateTime.from": "Van",
    "filter.dateTime.to": "Tot",
    "filter.dateTime.start": "Start",
    "filter.dateTime.end": "Einde",
    "filter.dateTime.time": "Tijd"
  }
}
</i18n>
